// @flow
import * as React from 'react';
import { Formik, Form } from 'formik';
import type { FormikActions } from 'formik';
import * as Yup from 'yup';
import cn from 'classnames';

//  Components
import { Container, Row, Col } from 'reactstrap';
import Button from 'components/atoms/Button';
import Modal from 'components/atoms/Modal';
import ForgotUsername from './components/ForgotUsername';
import ForgotPassword from './components/ForgotPassword';
import ContentFooter from 'components/pages/components/ContentFooter';
import { TextField, FormikField, PasswordField } from 'components/api';
import Alert from 'components/atoms/Alert';

// Types
import { gtmUserEventType } from 'types/gtm';

// Utils
import { isObjEmpty } from 'utils/varValidation';
import { clickEvent } from 'utils/adaConfigurations/customEvents';

import './SignIn.scss';

const ns = 'sign-in';

type Props = {
  doLogin: (username: string, password: string) => Object,
  gtmUserEvent: gtmUserEventType,
  intl: Object
};

type State = {
  showAlert: boolean
};

type Values = {
  userName: string,
  password: string
};

const AdditionalInfoSchema = Yup.object().shape({
  userName: Yup.string().required(),
  password: Yup.string().required()
});

class SignIn extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      showAlert: false
    };
    (this: any).forgotUsernameModal = React.createRef();
    (this: any).forgotPasswordModal = React.createRef();
    (this: any).forgotUsername = React.createRef();
    (this: any).forgotPassword = React.createRef();
    (this: any).userNameInput = React.createRef();
    (this: any).passwordInput = React.createRef();
    (this: any).getForgotUsernameModal = this.getForgotUsernameModal.bind(this);
    (this: any).getForgotPasswordModal = this.getForgotPasswordModal.bind(this);
    (this: any).onForgotUsernameClick = this.onForgotUsernameClick.bind(this);
    (this: any).onForgotPasswordClick = this.onForgotPasswordClick.bind(this);
    (this: any).onSubmit = this.onSubmit.bind(this);
  }

  initialValues = {
    userName: '',
    password: ''
  };
  forgotUsernameModal: React.Ref<any>;
  forgotPasswordModal: React.Ref<any>;
  forgotUsername: React.Ref<any>;
  forgotPassword: React.Ref<any>;
  userNameInput: React.Ref<'input'>;
  passwordInput: React.Ref<'input'>;

  onForgotUsernameClick() {
    const { gtmUserEvent } = this.props;
    this.setState({ showAlert: false });
    (this: any).forgotUsernameModal.current.setShowModal(true);
    gtmUserEvent(
      'Forgot Username Button',
      'Clicked',
      'display Forgot Username Modal',
      'GTM_CURRENT_PAGE > Forgot Username Modal'
    );
  }

  onForgotPasswordClick() {
    const { gtmUserEvent } = this.props;
    this.setState({ showAlert: false });
    (this: any).forgotPasswordModal.current.setShowModal(true);
    gtmUserEvent(
      'Forgot Password Button',
      'Clicked',
      'display Forgot Password Modal',
      'GTM_CURRENT_PAGE > Forgot Password Modal'
    );
  }

  onModalClose(modalContent: React.Ref<any>) {
    if (
      modalContent.current &&
      typeof modalContent.current.getWrappedInstance === 'function'
    ) {
      modalContent.current.getWrappedInstance().resetForm();
    }
  }

  createModal(modalProps: Object, body: () => React$Node): React$Node {
    return (
      <Modal className={`${ns}__modal`} {...modalProps}>
        {body()}
      </Modal>
    );
  }

  getForgotUsernameModal() {
    return this.createModal(
      {
        ref: this.forgotUsernameModal,
        onClose: () => this.onModalClose((this: any).forgotUsername)
      },
      () => (
        <ForgotUsername
          close={() => {
            (this: any).forgotUsernameModal.current.closeModal();
            this.setState({ showAlert: true });
          }}
          ref={this.forgotUsername}
          intl={this.props.intl}
        />
      )
    );
  }

  getForgotPasswordModal() {
    return this.createModal(
      {
        ref: this.forgotPasswordModal,
        onClose: () => this.onModalClose((this: any).forgotPassword)
      },
      () => (
        <ForgotPassword
          close={() => {
            (this: any).forgotPasswordModal.current.closeModal();
            this.setState({ showAlert: true });
          }}
          ref={this.forgotPassword}
          intl={this.props.intl}
        />
      )
    );
  }

  async onSubmit(values: Values, actions: FormikActions<Values>) {
    (this: any).userNameInput.current.blur();
    (this: any).passwordInput.current.blur();
    const { userName, password } = values;
    const { setSubmitting, setStatus } = actions;
    const { doLogin, gtmUserEvent } = this.props;
    const data = await doLogin(userName, password);
    const success = data && data.success;
    setSubmitting(false);
    setStatus({ success });
    gtmUserEvent(
      'Sign In Button',
      'Clicked',
      success ? 'Login' : 'error Login',
      success ? 'GTM_CURRENT_PAGE > User Vehicles Page' : 'GTM_CURRENT_PAGE'
    );
  }

  getEmailSuccessBanner() {
    const { showAlert } = this.state;
    if (showAlert) {
      return (
        <Alert color="primary" animation="top-down">
          Email successfully sent!
        </Alert>
      );
    }
    return null;
  }

  render() {
    const { intl } = this.props;
    const enterUserPass = intl.formatMessage({
      id: 'c8.sign.in.enter.username.password'
    });
    const signIn = intl.formatMessage({
      id: 'c8.sign.in.title'
    });

    return (
      <Formik
        initialValues={this.initialValues}
        validationSchema={AdditionalInfoSchema}
        onSubmit={this.onSubmit}
        render={({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          dirty,
          status
        }) => {
          const disabled = !dirty || !isObjEmpty(errors);
          const errorClassProps = cn(`${ns}__invalid-title`, {
            visible: status && !status.success,
            invisible: !status || status.success
          });
          return (
            <Container className={ns}>
              <main>
                {this.getEmailSuccessBanner()}
                {this.getForgotUsernameModal()}
                {this.getForgotPasswordModal()}
                <Row>
                  <Col
                    tabIndex={0}
                    aria-label={intl.formatMessage({
                      id: 'c8.aria.sign.in.panel',
                      defaultMessage: 'Sign In Panel'
                    })}
                    lg={{ size: 8, offset: 2 }}
                    className={`${ns}__card d-flex flex-column`}
                  >
                    <h1
                      className={`${ns}__title align-self-md-center mb-md-2 mb-lg-4 pt-lg-5`}
                    >
                      {signIn}
                    </h1>
                    <span className={`${errorClassProps} align-self-md-center`}>
                      {intl.formatMessage({
                        id: 'c8.common.invalid.username.or.email'
                      })}
                    </span>
                    <Form
                      className={'mt-lg-0 px-3 px-lg-5'}
                      onSubmit={handleSubmit}
                    >
                      <FormikField
                        tabIndex={0}
                        aria-label={intl.formatMessage({
                          id: 'c8.aria.user.name',
                          defaultMessage: 'User Name'
                        })}
                        renderedComponent={TextField}
                        className={`${ns}__username-field`}
                        name="userName"
                        placeholder={intl.formatMessage({
                          id: 'c8.common.username'
                        })}
                        htmlRef={this.userNameInput}
                      />
                      <FormikField
                        tabIndex={0}
                        aria-label={intl.formatMessage({
                          id: 'c8.aria.password',
                          defaultMessage: 'Password'
                        })}
                        renderedComponent={PasswordField}
                        className={`${ns}__password-field`}
                        name="password"
                        placeholder={intl.formatMessage({
                          id: 'c8.common.password'
                        })}
                        htmlRef={this.passwordInput}
                      />
                      <Button
                        tabIndex={0}
                        aria-label={intl.formatMessage({
                          id: 'c8.aria.sign.in',
                          defaultMessage: 'Sign In'
                        })}
                        className={`${ns}__signin-button d-flex justify-content-center ml-md-auto mt-md-4`}
                        disabled={disabled}
                        type="Submit"
                      >
                        <div className={`${ns}--search-button px-5 py-2`}>
                          {disabled ? enterUserPass : signIn}
                          {isSubmitting && (
                            <div className="ld ld-ring ld-spin-fast" />
                          )}
                        </div>
                      </Button>
                    </Form>
                    <div
                      className={`${ns}__forgot-buttons d-flex flex-row mt-auto py-3`}
                    >
                      <div
                        tabIndex={0}
                        aria-label={intl.formatMessage({
                          id: 'c8.aria.forgot.username',
                          defaultMessage: 'Forgot Username'
                        })}
                        className={`${ns}__forgot-username`}
                        onClick={this.onForgotUsernameClick}
                        onKeyUp={clickEvent}
                      >
                        {intl.formatMessage({
                          id: 'c8.common.forgot.username'
                        })}
                      </div>
                      <div
                        tabIndex={0}
                        aria-label={intl.formatMessage({
                          id: 'c8.aria.forgot.password',
                          defaultMessage: 'Forgot Password'
                        })}
                        className={`${ns}__forgot-password ml-auto`}
                        onClick={this.onForgotPasswordClick}
                        onKeyUp={clickEvent}
                      >
                        {intl.formatMessage({
                          id: 'c8.common.forgot.password'
                        })}
                      </div>
                    </div>
                  </Col>
                </Row>
              </main>
              <footer>
                <ContentFooter />
              </footer>
            </Container>
          );
        }}
      />
    );
  }
}

export default SignIn;



// WEBPACK FOOTER //
// ./src/components/pages/User/SignIn/SignIn.js